import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import Logo from '../../../components/Logo/Logo';
import { useRegistration } from '../../../hooks';
import { useAppDispatch } from '../../../store';
import { setEmail } from "../../../redux/slices/userProfileSlice";
import { Eye, EyeOff } from 'lucide-react';
import Toast from '../../../components/Toast/Toast';
import PasswordRequirements from '../../../components/common/PasswordRequirements';
import { getPasswordErrors } from '../../../utils/validation';
import { createUserWithEmailAndPassword, getIdToken, signOut } from 'firebase/auth';
import { auth } from '../../../firebase';
import { jwtDecode } from 'jwt-decode';
import apiVariables from '../../../services/api/apiVariables';
import authService from '../../../services/api/authService';
import { saveSensitiveData } from '../../../utils/secureStorage';
import config from '../../../config/config';

export default function CreateAccount() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { updateField, clearError } = useRegistration();
  
  const [email, setEmailLocal] = useState('');
  const [password, setPasswordLocal] = useState('');
  const [confirmPassword, setConfirmPasswordLocal] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'warning';
  } | null>(null);

  const isValidEmail = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const isAllowedEmailDomain = (value: string): boolean => {
    if (config.ALLOWED_EMAIL_DOMAINS.length === 0) return true;
    const domain = value.split('@')[1]?.toLowerCase();
    return !!domain && config.ALLOWED_EMAIL_DOMAINS.includes(domain);
  };

  const allowedDomainMessage = `Please use your email address ending with ${config.ALLOWED_EMAIL_DOMAINS.map((domain) => `@${domain}`).join(' or ')} to sign up.`;

  const passwordErrors = getPasswordErrors(password);

  const isButtonDisabled =
    !email.trim() ||
    !isValidEmail(email) ||
    !password.trim() ||
    !confirmPassword.trim() ||
    passwordErrors.length > 0 ||
    isChecking;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
   
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!isAllowedEmailDomain(email)) {
      setError(allowedDomainMessage);
      return;
    }

    if (passwordErrors.length > 0) {
      setError('Please meet all the password requirements listed below.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords and Confirm Password Should be same');
      return;
    }

    setIsChecking(true);
    setError(null);
    clearError();

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      const initialToken = await getIdToken(user, true);

      const response = await fetch(apiVariables.REGISTER_WITH_FIREBASE, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${initialToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to register user in database");
      }

      const token = await getIdToken(user, true);

      const decoded = jwtDecode<{ userId?: string; sub: string }>(token);

      if (!decoded.userId) {
        throw new Error("userId claim missing in token");
      }

      await saveSensitiveData("authToken", token);
      await saveSensitiveData("userId", decoded.userId);

      await authService.sendVerificationEmail(user);

      // Firebase auto-signs-in the newly created (unverified) user; sign them
      // back out so the app doesn't treat this as an authenticated session
      // until they've verified their email and logged in explicitly.
      await signOut(auth);

      dispatch(setEmail(email));
      updateField('email', email);
      
      setToast({
        message: 'Verification email sent. Please check your inbox.',
        type: 'success'
      });
      
      setTimeout(() => {
        navigate('/');
      }, 2000);
      
    } catch (error: any) {
      
      let errorMessage = 'An error occurred. Please try again.';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered. Please login instead.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please use a stronger password.';
      } else if (error.message) {
        try {
          const match = error.message.match(/\{\\?"error\\?":\{[^}]+\}\}/);
          if (match) {
            // Parse the escaped JSON string
            const jsonString = match[0].replace(/\\"/g, '"');
            const parsedError = JSON.parse(jsonString);
            if (parsedError.error && parsedError.error.message) {
              errorMessage = parsedError.error.message;
            }
          } else {
            errorMessage = error.message;
          }
        } catch{
          errorMessage = error.message;
        }
      }
      setError(errorMessage);
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className="flex min-h-screen shrink-0 items-stretch">

      {/* ── Left brand panel (desktop only) ── */}
      <div className="hidden self-stretch lg:flex lg:w-5/12 xl:w-1/2 flex-col items-center justify-center bg-gradient-to-br from-brand-primary to-brand-gray px-12 pt-12 pb-20 text-white">
        <div className="max-w-xs text-center">
          <div className="mb-3 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20">
            <svg viewBox="0 0 24 24" className="h-8 w-8 fill-white" aria-hidden="true">
              <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8Z" />
              <path d="M12 6a1 1 0 0 0-1 1v5.59l-3.71 3.7a1 1 0 1 0 1.42 1.42l4-4A1 1 0 0 0 13 13V7a1 1 0 0 0-1-1Z" />
            </svg>
          </div>
          <h2 className="mb-3 text-3xl font-bold">Join ISentryx</h2>
          <p className="text-base leading-relaxed text-white/75">
            Create your account and connect with IT professionals worldwide.
          </p>
          <div className="mt-10 flex justify-center gap-2">
            <div className="h-1.5 w-8 rounded-full bg-white/50" />
            <div className="h-1.5 w-2 rounded-full bg-white/30" />
            <div className="h-1.5 w-2 rounded-full bg-white/30" />
          </div>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex flex-1 flex-col items-center justify-start bg-gray-50 px-6 pt-12 pb-20">
        <div className="w-full max-w-sm">

          {/* Logo */}
          <div className="mb-8 flex justify-center">
            <div className="w-36 sm:w-44">
              <Logo />
            </div>
          </div>

          <h1 className="mb-1 text-2xl font-bold text-gray-950">Create Account</h1>
          <p className="mb-8 text-sm text-gray-600">Fill in your details to get started.</p>

          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Email */}
            <div>
              <label htmlFor="signup-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Email
              </label>
              <Input
                id="signup-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setEmailLocal(e.target.value); setError(null); }}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                required
              />
              {config.ALLOWED_EMAIL_DOMAINS.length > 0 && (
                <p className="mt-1.5 text-xs text-gray-500">{allowedDomainMessage}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="signup-password" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Password
              </label>
              <div className="relative">
                <Input
                  id="signup-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a password"
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setPasswordLocal(e.target.value); setError(null); }}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-10 text-sm outline-none transition focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <PasswordRequirements password={password} className="mt-2" />
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="signup-confirm" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Confirm Password
              </label>
              <div className="relative">
                <Input
                  id="signup-confirm"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setConfirmPasswordLocal(e.target.value); setError(null); }}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-10 text-sm outline-none transition focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Error banner */}
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Submit */}
            <Button
              type="submit"
              disabled={isButtonDisabled}
              className="inline-flex w-full items-center justify-center rounded-lg bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-hover disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {isChecking ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-600">
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => navigate('/')}
              className="font-semibold text-brand-primary transition-colors hover:text-brand-hover"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
